import { z } from "zod";
/**
 * @description 견적서 하단 산출 내역서 테이블 헤더
 */
export const survey_cost_headers = [
    { text: "No.", align: "start", value: "", width: "35px" },
    { text: "항목", align: "start", value: "cost_list" },
    { text: "단위", align: "center", value: "cost_unit", width: "50px" },
    { text: "수량", align: "end", value: "cost_num", width: "65px" },
    { text: "단가", align: "end", value: "cost_unit_price", width: "100px" },
    { text: "합계", align: "end", value: "cost_total", width: "110px" },
];
export const select_input_cost_headers = [
    { text: "No.", align: "start", value: "" },
    { text: "항목", align: "start", value: "cost_list" },
    { text: "단위", align: "start", value: "cost_unit" },
    { text: "수량", align: "start", value: "cost_num" },
    { text: "단가", align: "start", value: "cost_unit_price" },
    { text: "합계", align: "start", value: "cost_total" },
    {
        text: "행 숨김",
        align: "center",
        value: "actions",
    },
];
export const direct_input_cost_headers = [
    { text: "No.", align: "start", value: "" },
    { text: "항목", align: "start", value: "cost_list" },
    { text: "단위", align: "start", value: "cost_unit" },
    { text: "수량", align: "start", value: "cost_num" },
    { text: "단가", align: "start", value: "cost_unit_price" },
    { text: "합계", align: "start", value: "cost_total" },
    { text: "", align: "center", value: "actions" },
];
/**
 * @description 산출 내역서 세부 정보
 *
 * [아이템 기본 정보]
 * @param cost_list: 항목 이름
 * @param cost_unit: 단위
 *
 * [아이템 가격 정보 - ItemTotalPrice]
 * @param cost_num: 수량
 * @param cost_unit_price: 단가
 * @param cost_total: 합계
 *
 * [아이템 코드 관련 정보]
 * @param product_code: 완제품 코드
 * @param cost_list_sub: 완제품 규격
 * @param cost_num_editable: 수량 수정 가능 여부
 * @param cost_list_colspan: 항목 이름 colspan
 * @param cost_unit_price_editable: 단가 수정 가능 여부
 * @param allow_one_or_greater: 수량 1개 이상 가능 여부
 * @param deletable_row: 삭제 가능 여부
 * @param allow_edit_decimal_point: 소수점 수정 가능 여부
 */
/** 빈 행 colspan (구분선 역할, 순번 미표시, 번호 카운트 제외) */
export const BLANK_ROW_COLSPAN = 5;
export const CalcCostDetailDataSchema = z.lazy(() => ItemTotalDataSchema.extend({
    id: z.number().optional(),
    product_code: z.string().optional(),
    cost_list_sub: z.string().optional(),
    cost_num_editable: z.boolean().optional(),
    cost_list_btn: z
        .object({
        text: z.string(),
        click: z.function(),
    })
        .optional(),
    cost_list_colspan: z.number().optional(),
    cost_unit_price_editable: z.boolean().optional(),
    allow_one_or_greater: z.boolean().optional(),
    deletable_row: z.boolean().optional(),
    allow_edit_decimal_point: z.boolean().optional(),
    hidden: z.boolean().optional(),
    belong_data: z.array(CalcCostDetailDataSchema).optional(),
}).strict());
/**
 * @description 산출내역서 세부정보 아이템 가격정보 (수량, 단가)
 *
 * @params cost_list: 항목 이름
 * @params cost_unit: 단위
 * @params cost_num: 수량
 * @params cost_unit_price: 단가
 */
export const ItemTotalInfoSchema = z
    .object({
    cost_list: z.string(),
    cost_unit: z.string(),
})
    .strict();
export const ItemTotalPriceSchema = z
    .object({
    cost_num: z.coerce.number().nullable(),
    cost_unit_price: z.coerce.number().nullable(),
    cost_total: z.union([z.number(), z.string()]).nullable(),
})
    .strict();
export const ItemTotalDataSchema = ItemTotalInfoSchema.merge(ItemTotalPriceSchema);
export const defaultCalcCostDetailData = [
    {
        cost_list: "재료비",
        cost_list_colspan: 4,
        cost_unit: "식",
        cost_num: 0,
        cost_unit_price: 0,
        cost_total: 0,
        belong_data: [],
    },
    {
        cost_list: "노무비",
        cost_list_colspan: 4,
        cost_unit: "식",
        cost_num: 0,
        cost_unit_price: 0,
        cost_total: 0,
        belong_data: [
            {
                cost_list: "직접 노무비",
                cost_list_colspan: 4,
                cost_unit: "식",
                cost_num: 0,
                cost_unit_price: 0,
                cost_total: 0,
                belong_data: [],
            },
            {
                cost_list: "간접 노무비",
                cost_unit: "식",
                cost_num: 1,
                cost_unit_price: 0,
                cost_total: 0,
                belong_data: [
                    {
                        cost_list: "직접노무비X12.2%",
                        cost_list_colspan: 5,
                        cost_unit: "식",
                        cost_num: 0,
                        cost_unit_price: 0,
                        cost_total: 0,
                    },
                ],
            },
        ],
    },
    {
        cost_list: "경비",
        cost_list_colspan: 4,
        cost_unit: "식",
        cost_num: 0,
        cost_unit_price: 0,
        cost_total: 0,
        belong_data: [
            {
                cost_list: "고용보험료",
                cost_unit: "식",
                cost_num: 1,
                cost_num_editable: true,
                cost_unit_price: 0,
                cost_total: 0,
                belong_data: [
                    {
                        cost_list: "노무비X1.01%",
                        cost_list_colspan: 5,
                        cost_unit: "식",
                        cost_num: 0,
                        cost_unit_price: 0,
                        cost_total: 0,
                    },
                ],
            },
            {
                cost_list: "공구손료",
                cost_unit: "식",
                cost_num: 1,
                cost_num_editable: true,
                cost_unit_price: 0,
                cost_total: 0,
                belong_data: [
                    {
                        cost_list: "직접노무비X3%",
                        cost_list_colspan: 5,
                        cost_unit: "식",
                        cost_num: 0,
                        cost_unit_price: 0,
                        cost_total: 0,
                    },
                ],
            },
            {
                cost_list: "여비교통 통신비",
                cost_unit: "식",
                cost_num: 1,
                cost_num_editable: true,
                cost_unit_price: 0,
                cost_total: 0,
                belong_data: [
                    {
                        cost_list: "노무비X0.418%",
                        cost_list_colspan: 5,
                        cost_unit: "식",
                        cost_num: 0,
                        cost_unit_price: 0,
                        cost_total: 0,
                    },
                ],
            },
            {
                cost_list: "산재보험료",
                cost_unit: "식",
                cost_num: 1,
                cost_num_editable: true,
                cost_unit_price: 0,
                cost_total: 0,
                belong_data: [
                    {
                        cost_list: "노무비X3.7%",
                        cost_list_colspan: 5,
                        cost_unit: "식",
                        cost_num: 0,
                        cost_unit_price: 0,
                        cost_total: 0,
                    },
                ],
            },
            {
                cost_list: "세금과공과",
                cost_unit: "식",
                cost_num: 1,
                cost_num_editable: true,
                cost_unit_price: 0,
                cost_total: 0,
                belong_data: [
                    {
                        cost_list: "노무비X0.13%",
                        cost_list_colspan: 5,
                        cost_unit: "식",
                        cost_num: 0,
                        cost_unit_price: 0,
                        cost_total: 0,
                    },
                ],
            },
            {
                cost_list: "복리후생비",
                cost_unit: "식",
                cost_num: 1,
                cost_num_editable: true,
                cost_unit_price: 0,
                cost_total: 0,
                belong_data: [
                    {
                        cost_list: "노무비X2.397%",
                        cost_list_colspan: 5,
                        cost_unit: "식",
                        cost_num: 0,
                        cost_unit_price: 0,
                        cost_total: 0,
                    },
                ],
            },
            {
                cost_list: "퇴직공제 부금비",
                cost_unit: "식",
                cost_num: 1,
                cost_num_editable: true,
                cost_unit_price: 0,
                cost_total: 0,
                belong_data: [
                    {
                        cost_list: "직접노무비X2.3%",
                        cost_list_colspan: 5,
                        cost_unit: "식",
                        cost_num: 0,
                        cost_unit_price: 0,
                        cost_total: 0,
                    },
                ],
            },
            {
                cost_list: "소모품비",
                cost_unit: "식",
                cost_num: 1,
                cost_num_editable: true,
                cost_unit_price: 0,
                cost_total: 0,
                belong_data: [
                    {
                        cost_list: "노무비X1.479%",
                        cost_list_colspan: 5,
                        cost_unit: "식",
                        cost_num: 0,
                        cost_unit_price: 0,
                        cost_total: 0,
                    },
                ],
            },
            {
                cost_list: "산업안전보건관리비",
                cost_unit: "식",
                cost_num: 1,
                cost_num_editable: true,
                cost_unit_price: 0,
                cost_total: 0,
                belong_data: [
                    {
                        cost_list: "직접노무비X1.85%X1.2",
                        cost_list_colspan: 5,
                        cost_unit: "식",
                        cost_num: 0,
                        cost_unit_price: 0,
                        cost_total: 0,
                    },
                ],
            },
        ],
    },
    {
        cost_list: "일반관리비",
        cost_unit: "식",
        cost_num: 1,
        cost_unit_price: 0,
        cost_num_editable: true,
        allow_edit_decimal_point: true,
        cost_total: 0,
        belong_data: [
            {
                cost_list: "(재료비+노무비+경비)X6%",
                cost_list_colspan: 5,
                cost_unit: "식",
                cost_num: 0,
                cost_unit_price: 0,
                cost_total: 0,
            },
        ],
    },
    {
        cost_list: "이윤",
        cost_unit: "식",
        cost_num: 1,
        cost_num_editable: true,
        allow_edit_decimal_point: true,
        cost_unit_price: 0,
        cost_total: 0,
        belong_data: [
            {
                cost_list: "(노무비+경비+일반관리비)X15%",
                cost_list_colspan: 5,
                cost_unit: "식",
                cost_num: 0,
                cost_unit_price: 0,
                cost_total: 0,
            },
        ],
    },
    {
        cost_list: "절삭",
        cost_unit: "식",
        cost_num: 1,
        cost_num_editable: false,
        allow_edit_decimal_point: false,
        cost_unit_price: 0,
        cost_total: 0,
        cost_unit_price_editable: true,
        belong_data: [],
    },
];
